/**
 * Test utility for milestone reload functionality
 * This helps verify the AdminSegment to milestone conversion works correctly
 */

import { AdminSegmentStorage } from '../services/AdminSegmentStorage';
import { createInMemoryStorageLayer } from '../storage/storage-strategy';

/**
 * Test the milestone reload functionality
 */
export async function testMilestoneReload() {
  console.log('🧪 Testing milestone reload functionality...');
  
  // Create test storage
  const storage = createInMemoryStorageLayer();
  const adminSegmentStorage = new AdminSegmentStorage(storage.keyValue);
  
  // Mock contentService for testing
  const mockcontentService = {
    getContent: async (contentType: string, segmentId: string, languageId: string) => {
      console.log(`📥 Mock: Getting ${contentType} ${segmentId} (lang: ${languageId})`);
      
      // Return mock AdminSegment data based on segment ID
      if (segmentId === '11255') {
        // Standard milestones segment - create AdminSegmentDto and encode as base64
        const adminSegmentDto = {
          SurveybId: '11255',
          Title: 'Standard Milestones',
          LanguageTypeId: '1',
          SegmentType: 'PAS',
          SegmentTypeInfo: 'Standard milestone definitions',
          Pages: [], // Empty pages for test
          Nodes: [
            {
              Name: 'CDC_LC', // Communication node
              Order: [
                { Name: '1', Val: 'Says first word clearly' },
                { Name: '2', Val: 'Uses simple gestures like waving' },
                { Name: '3', Val: 'Follows simple instructions' }
              ],
              Params: [],
              Means: [],
              Probs: [],
              Cuts: [],
              Pages: []
            },
            {
              Name: 'CDC_MT', // Motor node
              Order: [
                { Name: '1', Val: 'Walks independently' },
                { Name: '2', Val: 'Climbs stairs with help' },
                { Name: '3', Val: 'Kicks a ball' }
              ],
              Params: [],
              Means: [],
              Probs: [],
              Cuts: [],
              Pages: []
            },
            {
              Name: 'CDC_CG', // Cognitive node
              Order: [
                { Name: '1', Val: 'Recognizes familiar faces' },
                { Name: '2', Val: 'Shows interest in books' },
                { Name: '3', Val: 'Points to body parts when named' }
              ],
              Params: [],
              Means: [],
              Probs: [],
              Cuts: [],
              Pages: []
            },
            {
              Name: 'CDC_SE', // Social-Emotional node
              Order: [
                { Name: '1', Val: 'Shows affection to familiar people' },
                { Name: '2', Val: 'Plays simple games like peek-a-boo' },
                { Name: '3', Val: 'Shows fear of strangers' }
              ],
              Params: [],
              Means: [],
              Probs: [],
              Cuts: [],
              Pages: []
            }
          ],
          Preloads: [],
          GeneratedAt: new Date().toISOString()
        };
        
        // Encode AdminSegmentDto as base64
        const jsonString = JSON.stringify(adminSegmentDto);
        const base64Content = typeof Buffer !== 'undefined' 
          ? Buffer.from(jsonString, 'utf-8').toString('base64')
          : btoa(jsonString);
        
        return {
          Name: 'content',
          Data: {
            Columns: ['content_body'],
            Rows: [[base64Content]]
          }
        };
      } else if (segmentId === '11270') {
        // Premium milestones segment - create AdminSegmentDto and encode as base64
        const adminSegmentDto = {
          SurveybId: '11270',
          Title: 'Premium Milestones',
          LanguageTypeId: '1',
          SegmentType: 'PAS',
          SegmentTypeInfo: 'Premium milestone definitions',
          Pages: [], // Empty pages for test
          Nodes: [
            {
              Name: 'CDC_LC_P', // Communication node (Premium)
              Order: [
                { Name: '1', Val: 'Uses complex sentences' },
                { Name: '2', Val: 'Tells detailed stories' }
              ],
              Params: [],
              Means: [],
              Probs: [],
              Cuts: [],
              Pages: []
            },
            {
              Name: 'CDC_CG_P', // Cognitive node (Premium)
              Order: [
                { Name: '1', Val: 'Solves complex problems' },
                { Name: '2', Val: 'Shows abstract thinking' }
              ],
              Params: [],
              Means: [],
              Probs: [],
              Cuts: [],
              Pages: []
            },
            {
              Name: 'CDC_MT_G', // Gross Motor node (Premium)
              Order: [
                { Name: '1', Val: 'Runs with coordination' },
                { Name: '2', Val: 'Jumps with both feet' }
              ],
              Params: [],
              Means: [],
              Probs: [],
              Cuts: [],
              Pages: []
            },
            {
              Name: 'CDC_MT_F', // Fine Motor node (Premium)
              Order: [
                { Name: '1', Val: 'Draws detailed pictures' },
                { Name: '2', Val: 'Uses utensils properly' }
              ],
              Params: [],
              Means: [],
              probs: [],
              Cuts: [],
              Pages: []
            },
            {
              Name: 'CDC_SE_P', // Social-Emotional node (Premium)
              Order: [
                { Name: '1', Val: 'Shows empathy' },
                { Name: '2', Val: 'Cooperates in group activities' }
              ],
              Params: [],
              Means: [],
              Probs: [],
              Cuts: [],
              Pages: []
            }
          ],
          Preloads: [],
          GeneratedAt: new Date().toISOString()
        };
        
        // Encode AdminSegmentDto as base64
        const jsonString = JSON.stringify(adminSegmentDto);
        const base64Content = typeof Buffer !== 'undefined' 
          ? Buffer.from(jsonString, 'utf-8').toString('base64')
          : btoa(jsonString);
        
        return {
          Name: 'content',
          Data: {
            Columns: ['content_body'],
            Rows: [[base64Content]]
          }
        };
      }
      
      console.warn(`⚠️ Mock: No data for segment ${segmentId}`);
      return null;
    }
  };
  
  try {
    // Test milestone extraction
    console.log('🔍 Testing milestone extraction...');
    const result = await adminSegmentStorage.extractMilestonesFromNodes(
      mockcontentService,
      '1',
      'standard'
    );
    
    console.log('📊 Extraction results:', {
      milestoneCount: result.milestones.length,
      textCount: result.milestoneTexts.length,
      sampleMilestone: result.milestones[0],
      sampleText: result.milestoneTexts[0]
    });
    
    // Test full reload
    console.log('🔄 Testing full milestone reload...');
    await adminSegmentStorage.reloadMilestonesFromNodes(
      mockcontentService,
      '1',
      true,  // include standard
      false  // exclude premium for test
    );
    
    console.log('✅ Milestone reload test completed successfully');
    return true;
  } catch (error) {
    console.error('❌ Milestone reload test failed:', error);
    return false;
  }
}

// Export for use in debugging
export { testMilestoneReload as default };